import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {WorkflowClient} from '@client/workflow-client'
import {MessageBox} from '@components/message-box'
import {Board} from '@extensions/board'
import {makeAutoObservable, observable, toJS} from 'mobx'
import {Lang} from 'yougile-lang'
import {
  DataObjectId,
  findIdInWorkflow,
  getDefaultWorkflow,
  Hub,
  makeNewWorkflow,
  updateWorkflowIds,
  Workflow,
  workflowToConfig
} from 'yougile-model'
import {logger, Util} from 'yougile-util'
import {humanReadableYamlError} from './yaml-util'

interface State {
  boardId: DataObjectId | undefined
  editedWorkflow: Workflow | undefined
  editedWorkflowId: string | undefined
  allWorkflows: {[id: string]: Workflow}
}

class SettignsPopupStore {
  state: State = {
    boardId: undefined,
    editedWorkflow: undefined,
    editedWorkflowId: undefined,
    allWorkflows: {}
  }

  constructor() {
    makeAutoObservable(this, {state: observable.shallow})
  }

  setBoard(boardId: DataObjectId | undefined): void {
    this.state.boardId = boardId
  }

  get board(): Hub {
    const box = Sync.getCurrentSandbox()
    return box.getHub(this.state.boardId)
  }

  get defaultConfig(): string {
    const columnNames = Board.listColumns(this.board, {
      noMirrors: true,
      sorted: true
    }).map(c => c.title)
    return getDefaultWorkflow(columnNames)
  }

  edit(workflow: Workflow): void {
    this.state.editedWorkflowId = workflow.id
    this.state.editedWorkflow = workflow
  }

  editNew(): void {
    this.state.editedWorkflowId = undefined
    const workflow = makeNewWorkflow()
    workflow.boards.push(this.state.boardId)
    this.state.editedWorkflow = workflow
  }

  save(): void {
    try {
      const box = Sync.getCurrentSandbox()
      const company = box.getHub(box.companyId)
      company.data.workflows ??= {}
      const workflow = toJS(this.state.editedWorkflow)
      const config = workflowToConfig(workflow)
      if (!config.stages) {
        MessageBox.warn(Lang.msg.workflow.stagesUndefined)
        return
      }
      const old = company.data.workflows[workflow.id]

      updateWorkflowIds(workflow, old)
      workflow.id = findIdInWorkflow(workflow.ids, 'workflow')
      workflow.name = workflowToConfig(workflow).name
      company.data.workflows[workflow.id] = workflow
      company.commit()
      this.stopEditing()
    } catch (e) {
      MessageBox.warn(
        [Lang.msg.workflow.failedToSave, humanReadableYamlError(e)]
          .filter(Boolean)
          .join(': ')
      )
      logger.error(e)
    }
  }

  remove(workflow: Workflow): void {
    const box = Sync.getCurrentSandbox()
    const company = box.getHub(box.companyId)
    company.data.workflows ??= {}
    delete company.data.workflows[workflow.id]
    company.commit()
    this.workflows
  }

  close(): void {
    this.stopEditing()
    this.setBoard(undefined)
  }

  stopEditing(): void {
    this.state.editedWorkflow = undefined
    this.state.editedWorkflowId = undefined
  }

  updateWorkflows(): void {
    this.state.allWorkflows = Util.clone(WorkflowClient.getCompanyWorkflows())
  }

  get workflows(): Workflow[] {
    return WorkflowClient.getBoardWorkflows(
      this.state.boardId,
      this.state.allWorkflows
    )
  }
}

export const popupStore = new SettignsPopupStore()

export const openWorkflowBoardSettings = (boardId: DataObjectId): void => {
  if (PermissionsChecker.isAdmin()) {
    popupStore.setBoard(boardId)
  }
}
